import { ReadCampaign } from '@tinct/core-api-models';
import {
  ActionMenuItem,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tinct/web-components';
import { ArrowUpFromLine, Ban, EllipsisVertical, SquarePen, Trash2 } from 'lucide-react';
import { isCampaignSetupComplete } from '../../../funnels/campaign-creation-funnel/get-campaign-setup-step';

/**
 * Single source of truth for which campaign actions are allowed for a given
 * status. Shared with the page header (Export URLs button). Rename is always
 * allowed, so it is not listed here.
 */
export function getCampaignActionCapabilities(campaign: ReadCampaign) {
  const isPublished = campaign.status === 'published';
  const isSnippetActive = campaign.snippetStatus?.status === 'active';
  // Needs `statistics.eventsCount` in the campaign projection; a projection
  // without it cannot tell and lets the backend (409) have the last word.
  const hasVisitorEvents = (campaign.statistics?.eventsCount ?? 0) > 0;

  return {
    export: {
      reason: !isPublished
        ? 'Campaign must be live to export its URLs'
        : !isSnippetActive
          ? 'Tinct snippet must be installed on your website to export URLs'
          : null,
    },
    // Publishable: unpublished/ready, or a draft already set up through validation
    // (the backend doesn't always flip draft→ready).
    canPublish:
      campaign.status === 'unpublished' ||
      campaign.status === 'ready' ||
      (campaign.status === 'draft' && isCampaignSetupComplete(campaign)),
    unpublish: {
      reason: isPublished ? null : 'Campaign must be live to unpublish',
    },
    // One policy everywhere (the list kebab mirrors this): a live campaign must
    // be unpublished first, and a campaign that visitors have already seen is
    // never deletable — its analytics are part of the account's history.
    delete: {
      reason: isPublished
        ? 'Unpublish the campaign before deleting it'
        : hasVisitorEvents
          ? 'This campaign has received visitor events and can no longer be deleted'
          : null,
    },
  };
}

export type CampaignActionsMenuProps = {
  campaign: ReadCampaign;
  onRename: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
};

export function CampaignActionsMenu({
  campaign,
  onRename,
  onPublish,
  onUnpublish,
  onDelete,
}: CampaignActionsMenuProps) {
  const capabilities = getCampaignActionCapabilities(campaign);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Open campaign actions menu">
          <EllipsisVertical className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onSelect={onRename}>
          <SquarePen />
          Rename
        </DropdownMenuItem>

        {capabilities.canPublish ? (
          <ActionMenuItem onSelect={onPublish}>
            <ArrowUpFromLine />
            Publish
          </ActionMenuItem>
        ) : (
          <ActionMenuItem onSelect={onUnpublish} blockedReason={capabilities.unpublish.reason}>
            <Ban />
            {campaign.landingPageOriginType === 'new' ? 'Unpublish page' : 'Unpublish'}
          </ActionMenuItem>
        )}

        <ActionMenuItem
          onSelect={onDelete}
          blockedReason={capabilities.delete.reason}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 />
          Delete
        </ActionMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
